<template>
  <div class="teacher-requests">
    <v-subheader class="py-0 d-flex justify-space-between rounded-lg">
      <TitleComponent title="Cereri modificare note" class="hide-on-mobile" />
      <v-spacer></v-spacer>
      <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
      <SuccessAlert :message="successMessage" @clear-error="clearMessage" />
      <TeacherRequestDialog 
        ref="requestDialog" 
        @success="handleSuccess"
        @error="handleError"
        @update="refreshData"
      />
    </v-subheader>
    <br />
    <v-row justify="end" v-if="isTeacher || isDean || isRector || isAdmin">
      <v-btn
        x-large
        @click="openNewRequestDialog"
        style="color: white; background-color: #2d8654"
      >
        Creare Cerere
      </v-btn>
    </v-row>
    <v-row justify="end" v-if="isAdmin || isOperator ">
      <v-dialog v-model="showDateRangePicker" persistent width="400">
        <template v-slot:activator="{ props }">
          <v-btn
            x-large
            v-bind="props"
            @click="showDateRangePicker = true"
            style="color: white; background-color: #2d8654"
          >
            Descarcă Raportul
          </v-btn>
        </template>
        <v-card>
          <v-card-title>
            <span class="text-h5">Selectează Intervalul de Date</span>
            <v-spacer></v-spacer>
            <v-btn icon dark color="black" @click="closeDateRangePicker">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-menu
                    ref="startMenu"
                    v-model="startMenu"
                    :close-on-content-click="false"
                    :return-value.sync="startDate"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedStartDate"
                        label="Data Începerii"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      no-title
                      scrollable
                      @change="saveStartDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-menu
                    ref="endMenu"
                    v-model="endMenu"
                    :close-on-content-click="false"
                    :return-value.sync="endDate"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedEndDate"
                        label="Data Încheierii"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      no-title
                      scrollable
                      @change="saveEndDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              class="mr-2"
              @click="downloadReport('minimal')"
            >
              Raport Minimal
            </v-btn>
            <v-btn
              color="secondary"
              @click="downloadReport('detailed')"
            >
              Raport Detaliat
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
    <br />

    <v-row>
      <v-col>
        <v-card>
          <v-card-title class="d-flex align-center pe-2">
            <div>&nbsp; Tabelul Cererilor</div>
            <v-spacer></v-spacer>
            <SearchBar @search="handleSearch" />
            <v-spacer></v-spacer>
            <ColumnSelect
              :headers="headers"
              :updateVisibleColumns="updateVisibleColumns"
            />
          </v-card-title>

          <v-data-table
            :mobile-breakpoint="0"
            :headers="visibleHeaders"
            :items="paginatedItems"
            :sort-by="pagination.sortBy"
            :sort-desc="pagination.sortDesc"
            :loading="isLoading"
            loading-text="Incărcare... Aștepta-ți vă rog"
            class="elevation-1"
            :options.sync="pagination"
            :server-items-length="filteredData.length"
            @update:options="handleTableOptionsUpdate"
            :multi-sort="false"
            :must-sort="true"
            sortable
          >
            <template v-slot:item="{ item, index }">
              <tr :class="{ 'light-green-row': index % 2 !== 0 }">
                <td
                  v-for="(header, hIndex) in visibleHeaders"
                  :key="hIndex"
                  :style="header.style ? header.style : {}"
                  :class="{ 'red-row': item.status === 'Respins' }"
                >
                  <template v-if="header.value === 'comments'">
                    <div v-if="item.id" class="comments-wrapper">
                      <v-timeline dense class="compact-timeline">
                        <v-timeline-item
                          v-for="(comment, index) in item.commentsHistory || []"
                          :key="index"
                          small
                          :color="getStatusColor(comment.status)"
                          class="compact-item"
                        >
                          <div class="comment-header">
                            <strong class="user-name">{{ comment.user }}</strong>
                            <v-chip x-small :color="getStatusColor(comment.status)" class="ml-1">
                              {{ comment.status }}
                            </v-chip>
                          </div>
                          <div class="comment-body">{{ comment.comment }}</div>
                          <div class="comment-date">{{ formatDate(comment.date) }}</div>
                        </v-timeline-item>
                      </v-timeline>
                    </div>
                    <div v-else class="no-comments">-</div>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :color="getStatusColor(item.status)" small>
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-else>
                    {{ getTableCellValue(item, header, index) }}
                  </template>
                  <template v-if="header.value === 'actions'">
                    <v-btn
                      color="primary"
                      outlined
                      small
                      class="mr-2"
                      @click="onViewClick(item)"
                    >
                      <v-icon small left>mdi-eye</v-icon>
                      Detalii
                    </v-btn>
                    <v-icon 
                      v-if="isAdmin" 
                      size="big" 
                      @click="deleteItem(item)"
                      class="ml-2"
                    >
                      mdi-delete
                    </v-icon>
                  </template>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Details component -->
    <TeacherRequestDetails
      ref="requestDetails"
      @update-data="refreshData"
      @success="handleSuccess"
      @error="handleError"
    />
    
    <DeleteItem
      ref="deleteItem"
      :request="selectedRequest"
      :visible="dialogDelete"
      @updateRequestData="refreshData"
      @error="handleError"
      @success="handleSuccess"
    />
  </div>
</template>

<script>
import TitleComponent from "@/components/elements/TitleComponent.vue";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import TeacherRequestDialog from "./TeacherRequestDialog.vue";
import TeacherRequestDetails from "./TeacherRequestDetails.vue";
import DeleteItem from "./DeleteItem.vue";
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import { mapGetters } from "vuex";

export default {
  name: "TeacherRequestsPage",
  components: {
    TitleComponent,
    ColumnSelect,
    ErrorAlert,
    SuccessAlert,
    SearchBar,
    TeacherRequestDialog,
    TeacherRequestDetails,
    DeleteItem
  },
  data() {
    return {
      headers: [],
      requestsData: [],
      visibleHeaders: [],
      search: "",
      sortBy: "date",
      sortDesc: true,
      errorMessage: "",
      successMessage: "",
      isLoading: true,
      startDate: '',
      endDate: '',
      formattedStartDate: '',
      formattedEndDate: '',
      startMenu: false,
      endMenu: false,
      showDateRangePicker: false,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["date"],
        sortDesc: [true]
      },
      commentsCache: {},
      commentLoading: false,
      dialogDelete: false,
      selectedRequest: null,
    };
  },
  watch: {
    startDate(newValue) {
      this.formattedStartDate = newValue ? newValue : '';
    },
    endDate(newValue) {
      this.formattedEndDate = newValue ? newValue : '';
    },
    'pagination.page': function() {
      this.fetchCommentsForVisibleItems();
    },
    pagination: {
      handler(newVal, oldVal) {
        if (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          this.fetchCommentsForVisibleItems();
        }
      },
      deep: true
    },
    filteredData: {
      handler() {
        this.$nextTick(() => {
          if (!this.isLoading && this.paginatedItems.length > 0) {
            this.fetchCommentsForVisibleItems();
          }
        });
      }
    }
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.getUser.role === "admin";
    },
    isTeacher() {
      return this.getUser.role === "profesor" || this.getUser.isTeacher;
    },
    isDean() {
      return this.getUser.role === "usersef";
    },
    isRector() {
      return this.getUser.role === "rector";
    },
    isOperator() {
      return this.getUser.role === "operator";
    },
    filteredData() {
      if (!this.requestsData || !this.requestsData.length) return [];
      
      return this.requestsData.filter(item => {
        if (!this.search) return true;
        
        const searchLower = this.search.toLowerCase();
        return (
          (item.user && item.user.toLowerCase().includes(searchLower)) ||
          (item.facultatea && item.facultatea.toLowerCase().includes(searchLower)) ||
          (item.grupa && item.grupa.toLowerCase().includes(searchLower)) ||
          (item.disciplina && item.disciplina.toLowerCase().includes(searchLower)) ||
          (item.cause && item.cause.toLowerCase().includes(searchLower)) ||
          (item.status && item.status.toLowerCase().includes(searchLower))
        );
      });
    },
    paginatedItems() {
      let sortedData = [...this.filteredData];
      
      if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
        const sortKey = this.pagination.sortBy[0];
        const sortDesc = this.pagination.sortDesc[0];
        
        sortedData.sort((a, b) => {
          let aValue, bValue;
          
          if (sortKey === 'date') {
            aValue = new Date(a.date || a.created_at).getTime();
            bValue = new Date(b.date || b.created_at).getTime();
          } else if (sortKey === 'id' || sortKey === 'requestId') {
            aValue = parseInt(a[sortKey]);
            bValue = parseInt(b[sortKey]);
          } else {
            aValue = a[sortKey];
            bValue = b[sortKey];
          }
          
          if (aValue < bValue) return sortDesc ? 1 : -1;
          if (aValue > bValue) return sortDesc ? -1 : 1;
          return 0;
        });
      }
      
      const startIndex = (this.pagination.page - 1) * this.pagination.itemsPerPage;
      const endIndex = startIndex + this.pagination.itemsPerPage;
      return sortedData.slice(startIndex, endIndex);
    }
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      
      try {
        // Role-based data fetching
        if (this.isAdmin || this.isOperator || this.isRector) {
          // Admin, operator, and rector see all requests
          this.requestsData = await api.getAllTeacherRequests();
        } else if (this.isTeacher) {
          // Teachers see only their own requests
          this.requestsData = await api.getTeacherRequestsByUserId(this.getUser.userId);
        } else if (this.isDean) {
          // Deans see requests from their department and those waiting for their approval
          this.requestsData = await api.getTeacherRequestsByDepartment(this.getUser.department);
        } else {
          this.requestsData = [];
        }
        
        // Map the requests data to include an empty commentsHistory property
        this.requestsData = this.requestsData.map(item => ({
          ...item,
          commentsHistory: this.commentsCache[item.id] || []
        }));
        
        // Fetch comments for visible items
        await this.fetchCommentsForVisibleItems();
        
      } catch (error) {
        console.error("Error fetching teacher requests:", error);
        this.handleError("Nu s-au putut încărca cererile. Încercați din nou mai târziu.");
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchCommentsForVisibleItems() {
      if (this.commentLoading) return;
      
      try {
        this.commentLoading = true;
        
        const visibleItems = this.paginatedItems;
        
        const requestIdsToFetch = visibleItems
          .filter(item => item.id && !this.commentsCache[item.id])
          .map(item => item.id);
        
        if (requestIdsToFetch.length === 0) {
          this.commentLoading = false;
          return;
        }
        
        const commentsData = await api.getBulkTeacherRequestCommentsHistory(requestIdsToFetch);
        
        Object.entries(commentsData).forEach(([requestId, comments]) => {
          const numericId = parseInt(requestId, 10);
          this.commentsCache[numericId] = comments;
          
          const item = this.requestsData.find(item => item.id === numericId);
          if (item) {
            item.commentsHistory = comments;
          }
        });
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        this.commentLoading = false;
      }
    },
    
    async refreshData() {
      await this.fetchData();
    },
    
    updateVisibleColumns() {
      // Implement different visible columns based on role
      if (this.isAdmin || this.isOperator || this.isRector || this.isDean) {
        this.visibleHeaders = this.headers.filter((header) => header.visible);
      } else {
        this.visibleHeaders = this.headers.filter((header) => header.visible && header.notForUser);
      }
    },
    
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    
    getTableCellValue(item, header, index) {
      if (header.value === "index") return index + 1;
      if (header.value === "user") return item.user;
      if (header.value === "facultatea") return item.facultatea;
      if (header.value === "grupa") return item.grupa;
      if (header.value === "disciplina") return item.disciplina;
      if (header.value === "cause") return item.cause;
      if (header.value === "status") return item.status;
      if (header.value === "date") return this.formatCreatedAt(item.date);
      if (header.value === "actions") return "";
      return item[header.value] || '';
    },
    
    formatCreatedAt(dateString) {
      return helper.formatCreatedAt(dateString);
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getStatusColor(status) {
      const statusColors = {
        'Respins': 'error',
        'Emis de profesor': 'primary',
        'Aprobat de Decan': 'info',
        'Aprobat de Rector': 'purple',
        'Îndeplinit': 'success',
        'În proces': 'warning'
      };
      
      if (!status) return 'grey';
      
      for (const [key, value] of Object.entries(statusColors)) {
        if (status.includes(key)) {
          return value;
        }
      }
      
      return 'grey';
    },
    
    handleTableOptionsUpdate(options) {
      const sortBy = options.sortBy && options.sortBy.length ? options.sortBy : ["date"];
      const sortDesc = options.sortDesc && options.sortDesc.length ? options.sortDesc : [true];
      
      this.pagination = {
        page: options.page || 1,
        itemsPerPage: options.itemsPerPage || 10,
        sortBy: sortBy,
        sortDesc: sortDesc
      };
      
      this.sortBy = this.pagination.sortBy[0] || "date";
      this.sortDesc = this.pagination.sortDesc[0] !== false;
      
      localStorage.setItem('teacherRequestPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
      
      this.$nextTick(() => {
        this.fetchCommentsForVisibleItems();
      });
    },
    
    openNewRequestDialog() {
      this.$refs.requestDialog.open();
    },
    
    onViewClick(item) {
      if (!item || !item.id) {
        this.handleError("Nu s-a putut deschide cererea. ID lipsă.");
        return;
      }
      this.$refs.requestDetails.open({
        requestId: item.id,
        ...item
      });
    },
    
    deleteItem(item) {
      this.selectedRequest = { ...item };
      this.$refs.deleteItem.show();
    },
    
    saveStartDate(date) {
      this.startDate = date;
      this.formattedStartDate = date;
      this.$refs.startMenu.save(date);
    },
    
    saveEndDate(date) {
      this.endDate = date;
      this.formattedEndDate = date;
      this.$refs.endMenu.save(date);
    },
    
    closeDateRangePicker() {
      this.showDateRangePicker = false;
    },
      async downloadReport(reportType = 'detailed') {
      this.isLoading = true;
      this.errorMessage = "";
      this.successMessage = "";

      // Validate dates before proceeding
      if (!this.startDate || !this.endDate) {
        this.errorMessage = "Vă rugăm să selectați ambele date pentru a genera raportul.";
        this.isLoading = false;
        return;
      }      // Convert date strings to ISO format YYYY-MM-DD
      const formatDate = (date) => {
        if (!date) return '';
        // Handle both cases where date might already be YYYY-MM-DD or include time
        const dateObj = new Date(date);
        return dateObj.toISOString().split('T')[0];
      };

      const params = {
        startDate: formatDate(this.startDate),
        endDate: formatDate(this.endDate),
      };

      try {
        const response = await api.downloadTeacherReport(params, reportType); // Pass reportType

        // Create a Blob from the response data
        const blob = new Blob([response.data], { type: response.headers['content-type'] });

        // Create a link element
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);

        // Extract filename from Content-Disposition header if available, otherwise create a default
        const contentDisposition = response.headers['content-disposition'];
        let filename = `TeacherReport_${params.startDate}_${params.endDate}_${reportType}.xlsx`; // Default filename
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch && filenameMatch.length === 2) {
            filename = filenameMatch[1];
          }
        }
        
        link.download = filename;

        // Append link to the body, click it, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href); // Clean up the object URL

        this.successMessage = `Raportul (${reportType}) a fost descărcat cu succes.`;
        this.closeDateRangePicker(); // Close dialog on success
      } catch (error) {
        console.error(`Error downloading ${reportType} report:`, error);
        // Log detailed error information
        console.error('Download Error Response:', error.response); 
        console.error('Download Error Request:', error.request);
        console.error('Download Error Message:', error.message);
        
        // Attempt to parse error response if it's a blob containing JSON
        if (error.response && error.response.data instanceof Blob && error.response.data.type.includes('json')) {
          try {
            const errorJsonText = await error.response.data.text();
            const errorJson = JSON.parse(errorJsonText);
            console.error('Backend Error Details:', errorJson);
            this.errorMessage = `Eroare Server: ${errorJson.message || 'Mesaj de eroare neclar.'}`;
          } catch (parseError) {
            console.error('Could not parse error blob:', parseError);
            this.errorMessage = `A apărut o eroare la descărcarea raportului (${reportType}). Verificați consola browserului (F12) pentru detalii.`;
          }
        } else if (error.response && error.response.data && typeof error.response.data === 'object' && error.response.data.message) {
          // Handle standard JSON error response
          this.errorMessage = `Eroare: ${error.response.data.message}`;
        } 
        else {
          // Generic error message if specific details aren't available
          this.errorMessage = `A apărut o eroare la descărcarea raportului (${reportType}). Verificați consola browserului (F12) pentru detalii.`;
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    handleSuccess(message) {
      this.successMessage = message;
    },
    
    handleError(message) {
      this.errorMessage = message;
    },
    
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    }
  },
  mounted() {
    // Load headers configuration from the JSON file
    this.headers = require("./teacherRequestsData.json").map(header => ({
      ...header,
      sortable: header.value !== 'actions' && header.value !== 'comments',
      notForUser: true // All columns are visible for users
    }));
    
    // Set up header visibility based on role
    if (!(this.isAdmin || this.isOperator || this.isRector || this.isDean)) {
      this.visibleHeaders = this.headers.filter(
        (header) => header.defaultVisible && header.notForUser 
      );
    } else {
      this.visibleHeaders = this.headers.filter(
        (header) => header.defaultVisible
      );
    }
    
    // Set visibility flag for each header
    this.headers.forEach((header) => {
      header.visible = header.defaultVisible;
    });
    
    // Set up event listeners
    this.$refs.deleteItem.$on("error", this.handleError);
    this.$refs.deleteItem.$on("success", this.handleSuccess);

    // Restore pagination settings from localStorage
    const savedPaginationSettings = localStorage.getItem('teacherRequestPaginationSettings');
    if (savedPaginationSettings) {
      try {
        const settings = JSON.parse(savedPaginationSettings);
        this.pagination = {
          page: settings.page || 1,
          itemsPerPage: settings.itemsPerPage || 10,
          sortBy: settings.sortBy || ["date"],
          sortDesc: settings.sortDesc || [true]
        };
        this.sortBy = this.pagination.sortBy[0] || "date";
        this.sortDesc = this.pagination.sortDesc[0] !== false;
      } catch (e) {
        console.error('Error parsing saved pagination settings:', e);
      }
    }
    
    // Fetch data
    this.fetchData();
    
    // Watch for window unload to save pagination settings
    window.addEventListener('beforeunload', () => {
      localStorage.setItem('teacherRequestPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
    });
    
    this.$watch('$vuetify.breakpoint.name', () => {
      this.$nextTick(() => {
        this.fetchCommentsForVisibleItems();
      });
    });
  }
};
</script>

<style scoped>
.teacher-requests .v-data-table::v-deep tbody td {
  font-size: 18px;
}
.v-data-table::v-deep th {
  font-size: 20px !important;
  background-color: #2d8659 !important;
  color: white !important;
  white-space: nowrap;
}
.light-green-row {
  background-color: #ecf9f2;
}
.red-row {
  background-color: #f29daa;
}
@media (max-width: 600px) {
  .hide-on-mobile {
    display: none;
  }
}
.comments-wrapper {
  max-height: 150px;
  overflow-y: auto;
  padding: 4px;
  min-height: 60px; /* Increased minimum height to prevent layout shifts */
}

.compact-timeline {
  padding-top: 0;
}

.compact-item {
  padding-bottom: 8px !important;
}

.comment-header {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.user-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.comment-body {
  font-size: 0.85rem;
  color: #666;
  margin: 2px 0;
  white-space: normal;
  word-break: break-word;
}

.comment-date {
  font-size: 0.75rem;
  color: #999;
}

.no-comments {
  text-align: center;
  color: #999;
  padding: 10px;
  font-style: italic;
}
</style>